import {useEffect, useState} from 'react';
import {
    AspectRatio,
    Badge,
    Card,
    Container,
    Group,
    SimpleGrid,
    Spoiler,
    Stack,
    Text,
    TextInput,
    Title,
    useMantineTheme
} from '@mantine/core';
import {useConfig, DemosItem} from '../ConfigContext';
import classes from './Demos.module.css';

/**
 * The archive. These are recordings of the studio's first platform, which is
 * retired — they are kept because two of the ventures in the portfolio started
 * as prototypes you can watch here.
 */
function Demos() {
    const theme = useMantineTheme();
    const {demosCategories, demos} = useConfig();
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [filteredDemos, setFilteredDemos] = useState(demos);

    useEffect(() => {
        const lowercaseFilter = searchQuery.toLowerCase();
        const filteredBySearch = demos.filter(item =>
            item.title.toLowerCase().includes(lowercaseFilter) ||
            item.abstract?.toLowerCase().includes(lowercaseFilter) ||
            item.categoryId.toLowerCase().includes(lowercaseFilter)
        );

        const filteredByCategory = selectedCategory
            ? filteredBySearch.filter(item => item.categoryId === selectedCategory)
            : filteredBySearch;

        setFilteredDemos(filteredByCategory);
    }, [searchQuery, selectedCategory, demos]);

    const renderDemosItem = (item: DemosItem) => (
        <Card withBorder radius="md" className={classes.card} key={item.assetId}>
            <Card.Section style={{padding: "15px"}}>
                <AspectRatio ratio={16 / 9} mx="auto">
                    <iframe
                        src={item.image}
                        title={item.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                    />
                </AspectRatio>
            </Card.Section>
            <Group gap="xs" mt="xs">
                <Badge variant="light" color="gray">{demosCategories[item.categoryId]?.title}</Badge>
                {item.date && <Badge variant="light" color="gray">{item.date}</Badge>}
            </Group>
            <Text className={classes.title} fw={600} style={{marginTop: 10}}>
                {item.title}
            </Text>
            <Text fz="sm" c="dimmed" style={{whiteSpace: 'pre-line'}}>
                <Spoiler hideLabel={"less"} showLabel={"more"} maxHeight={72}>
                    {item.abstract}
                </Spoiler>
            </Text>
        </Card>
    );

    return (
        <Container py="xl" size="xl">
            <Stack gap="sm" mb="xl">
                <Title order={1} c={theme.colors.brand[6]}>From the archive</Title>
                <Text size="lg" maw={760}>
                    Before the portfolio, there was one platform. These recordings are from it.
                    It is retired now, but two of our ventures began as prototypes you can watch
                    here — so we keep them rather than quietly deleting them.
                </Text>
            </Stack>

            <TextInput
                radius="xl"
                placeholder="Search the archive"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.currentTarget.value)}
                style={{marginBottom: '20px'}}
            />
            <Group style={{justifyContent: 'center', margin: '10px 0'}}>
                {Object.keys(demosCategories).map((category) => (
                    <Badge
                        key={category}
                        color={selectedCategory === category ? 'blue' : 'gray'}
                        onClick={() => setSelectedCategory(category)}
                        style={{cursor: 'pointer'}}
                    >
                        {demosCategories[category].title}
                    </Badge>
                ))}
                <Badge color="red" onClick={() => setSelectedCategory('')} style={{cursor: 'pointer'}}>
                    Clear Filter
                </Badge>
            </Group>

            <SimpleGrid cols={{base: 1, sm: 2, lg: 3}} spacing="lg">
                {filteredDemos.map(renderDemosItem)}
            </SimpleGrid>
        </Container>
    );
}

export default Demos;
